import LoadingButton from "@mui/lab/LoadingButton";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API_URL, handleSendMessage } from "../../utilities";
import { getRoutes } from "../../utilities/routes";
import { useAuth } from "../../context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import React, { useEffect, useState, useCallback } from "react";
import TabInfoUser from "./components/tab-info-user";
import { TypeUser } from "../../interface/enum/TypeUser";
import { IAppUser } from "../../interface/IUser";
import { IAutoCompleteLabelValue } from "../../interface/enum/GeneralType";

// اعتبارسنجی کد ملی - بررسی فرمت 10 رقمی
const nationalCodeValidation = Yup.string()
  .matches(/^\d{10}$/, "کد ملی باید 10 رقم باشد")
  .required("این فیلد الزامی است");

// اعتبارسنجی رمز عبور - حداقل 12 کاراکتر با ترکیب حروف و نمادها
const passwordValidation = Yup.string()
  .min(12, "پسورد باید حداقل ۱۲ کاراکتر باشد")
  .matches(/[a-z]/, "پسورد باید شامل حداقل یک حرف کوچک انگلیسی باشد")
  .matches(/[A-Z]/, "پسورد باید شامل حداقل یک حرف بزرگ انگلیسی باشد")
  .matches(
    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/,
    "پسورد باید شامل حداقل یک نماد خاص باشد"
  )
  .matches(/^(?!\d+$).*$/, "پسورد نمی‌تواند فقط از اعداد تشکیل شود");

// Schema اعتبارسنجی فرم
const validationSchema = Yup.object({
  fName: Yup.string().required("این فیلد الزامی است"),
  lName: Yup.string().required("این فیلد الزامی است"),
  roleName: Yup.string().required("این فیلد الزامی است"),
  caseNumber: Yup.string().nullable(),
  postalCode: Yup.string()
    .matches(/^\d{10}$/, "کد پستی باید 10 رقم باشد")
    .nullable(),
  nationalCode: nationalCodeValidation,
  password: passwordValidation,
  newPassword: passwordValidation,
  phoneNumber: Yup.string()
    .required("این فیلد الزامی است")
    .matches(/^\d{11}$/, "شماره تماس باید 11 رقم باشد"),
});

// مقادیر اولیه فرم
const initialValues = {
  cityId: undefined,
  provinceId: undefined,
  meetingMethodRef: undefined,
  tagRef: [],
  targetTypeRef: undefined,
  baseActivityRef: undefined,
  referrerRef: undefined,
  isSendBalanceSms: "true",
  taxAreaCode: "",
  caseNumber: "",
  economicCode: "",
  economicCode_OLD: "",
  folderPath: "",
  postalCode: "",
  address: "",
  gender: undefined,
  typeUser: "true",
  coType: undefined,
  isActive: "true",
  fName: "",
  lName: "",
  fatherName: "",
  telNumber: "",
  nationalIDBack: "",
  coName: "",
  coTelNumber: "",
  coBaseActivity: "",
  coTaxFileClass: "",
  parentRef: undefined,
  roleTitle: "",
  nationalCode: "",
  nationalId: "",
  codePersonel: "",
  desc: "",
  birthDate: "",
  userName: "",
  email: "",
  emailConfirmed: 0,
  password: "",
  newPassword: undefined,
  confirmedPassword: "",
  phoneNumber: "",
  phoneNumberConfirmed: 0,
  roleName: TypeUser.CUSTOMER,
};

interface NewUserProps {
  open: boolean;
  setOpenBank: (open: boolean) => void;
  refetch: () => void;
  isEdit?: boolean;
  userRef?: string;
  userRole?: string;
  setUserId?: any;
  usersData: IAppUser[];
  tags: IAutoCompleteLabelValue[];
  copiedUserData?: IAppUser | null;
}

const NewUser = ({
  open,
  setOpenBank,
  refetch,
  usersData,
  isEdit = false,
  setUserId,
  userRef,
  userRole,
  tags,
  copiedUserData,
}: NewUserProps) => {
  const notifications = useNotifications();
  const { user } = useAuth();
  const Routes = getRoutes();

  // Mutation برای ایجاد کاربر جدید
  const { mutateAsync: insertAsync, isPending } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(Routes.UsersRoutes, {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      if (responseData.status === true) {
        handleSendMessage(notifications, "کاربر جدید ذخیره شد", "success");
        refetch(); // به‌روزرسانی لیست کاربران
      } else {
        if (responseData.data === "شماره پرونده تکراری است!") {
          handleSendMessage(notifications, "شماره پرونده تکراری است!", "error");
        } else {
          handleSendMessage(notifications, "خطا در ذخیره سازی!", "error");
        }
      }
    },
    onError: (error) => {
      console.log("خطا در ایجاد کاربر:", error);
      handleSendMessage(notifications, "خطا در ذخیره سازی!", "error");
    },
  });

  // Mutation برای ویرایش کاربر
  const { mutateAsync: updateAsync, isPending: isEditLoading } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(Routes.UsersRoutes + body.id, {
        method: "PUT",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      if (responseData.status === true) {
        handleSendMessage(
          notifications,
          "اطلاعات کاربر بروزرسانی شد",
          "success"
        );
        refetch(); // به‌روزرسانی لیست کاربران
      } else {
        if (responseData.data === "شماره پرونده تکراری است!") {
          handleSendMessage(notifications, "شماره پرونده تکراری است!", "error");
        } else {
          handleSendMessage(notifications, "خطا در بروزرسانی!", "error");
        }
      }
    },
    onError: (error) => {
      notifications.show("خطا در هنگام ویرایش کاربر" + error.message, {
        severity: "error",
        autoHideDuration: 3000,
      });
      console.log("خطا در ویرایش کاربر:", error);
    },
  });

  // Mutation برای حذف کاربر
  const { mutateAsync: deletedUserAsync, isPending: isDeletedUserLoading } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(Routes.UsersRoutes + body.id, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        });
        return await response.json();
      },
      onSuccess: (responseData) => {
        handleSendMessage(notifications, "کاربر حذف شد", "success");
        refetch(); // به‌روزرسانی لیست کاربران
      },
      onError: (error) => {
        notifications.show("خطا در حذف کاربر: " + error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
        console.log("خطا در حذف کاربر:", error);
      },
    });

  // Mutation برای بررسی یکتا بودن شماره پرونده
  const { mutateAsync: checkCaseNumberUnique } = useMutation({
    mutationFn: async ({
      caseNumber,
      excludeUserId,
    }: {
      caseNumber: string;
      excludeUserId?: string;
    }) => {
      const url = excludeUserId
        ? `${Routes.UsersRoutes}checkCaseNumber/${caseNumber}?excludeUserId=${excludeUserId}`
        : `${Routes.UsersRoutes}checkCaseNumber/${caseNumber}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      });
      return await response.json();
    },
  });

  // Query برای دریافت اطلاعات کاربر خاص (با کش کردن)
  const {
    data: userData,
    refetch: refetchUserData,
    isLoading: userFetchLoading,
  } = useQuery({
    queryKey: ["user", userRef, copiedUserData?.id],
    queryFn: async () => {
      if (!userRef && !copiedUserData?.id) return null;
      const userId = userRef || copiedUserData?.id;
      const response = await fetch(Routes.UsersRoutes + userId, {
        method: "GET",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
      });
      return await response.json();
    },
    enabled: (!!userRef && isEdit) || !!copiedUserData?.id, // در حالت ویرایش یا کپی اجرا شود
    staleTime: 5 * 60 * 1000, // 5 دقیقه کش
    gcTime: 10 * 60 * 1000, // 10 دقیقه نگهداری در کش
  });

  // تعیین اینکه آیا در حالت ویرایش هستیم یا کپی
  const isActuallyEdit = isEdit && !copiedUserData;

  // تابع بستن دیالوگ
  const handleClose = useCallback(() => {
    setOpenBank(false);
    formik.resetForm();
    refetch();
    setUserId("");
  }, [setOpenBank, refetch, setUserId]);

  // تنظیم فرم با داده‌های کپی شده
  useEffect(() => {
    if (copiedUserData && open) {
      // تبدیل تگ‌ها به فرمت مورد نیاز
      const userTags = copiedUserData.tagRef
        ? copiedUserData.tagRef.split(",").map((tagId: string) => {
            const tag = tags.find((t) => t.value === parseInt(tagId));
            return tag || { label: tagId, value: parseInt(tagId) };
          })
        : [];

      formik.setValues({
        ...initialValues,
        fName: copiedUserData.fName || "",
        lName: copiedUserData.lName || "",
        phoneNumber: copiedUserData.phoneNumber || "",
        nationalCode: copiedUserData.nationalCode || "",
        email: copiedUserData.email || "",
        telNumber: copiedUserData.telNumber || "",
        address: copiedUserData.address || "",
        postalCode: copiedUserData.postalCode || "",
        coName: copiedUserData.coName || "",
        coTelNumber: copiedUserData.coTelNumber || "",
        coBaseActivity: copiedUserData.coBaseActivity || "",
        roleTitle: copiedUserData.roleTitle || "",
        economicCode: copiedUserData.economicCode || "",
        economicCode_OLD: copiedUserData.economicCode_OLD || "",
        folderPath: copiedUserData.folderPath || "",
        caseNumber: copiedUserData.caseNumber || "",
        fatherName: copiedUserData.fatherName || "",
        nationalIDBack: copiedUserData.nationalIDBack || "",
        nationalId: copiedUserData.nationalId || "",
        codePersonel: copiedUserData.codePersonel || "",
        taxAreaCode: copiedUserData.taxAreaCode || "",
        coTaxFileClass: copiedUserData.coTaxFileClass || "",
        desc: copiedUserData.desc || "",
        gender: (copiedUserData.gender?.toString() as any) || "false",
        typeUser: copiedUserData.typeUser?.toString() || "true",
        coType: (copiedUserData.coType?.toString() as any) || "false",
        roleName: copiedUserData.roleName || TypeUser.CUSTOMER,
        isSendBalanceSms: copiedUserData.isSendBalanceSms?.toString() || "true",
        isActive: copiedUserData.isActive ? "true" : "false",
        cityId: (copiedUserData.cityId as any) || undefined,
        provinceId: (copiedUserData.provinceId as any) || undefined,
        meetingMethodRef: (copiedUserData.meetingMethodRef as any) || undefined,
        tagRef: userTags as any,
        baseActivityRef: (copiedUserData.baseActivityRef as any) || undefined,
        referrerRef: (copiedUserData.referrerRef as any) || undefined,
        birthDate: copiedUserData.birthDate
          ? new Date(copiedUserData.birthDate).toISOString()
          : "",
      });
    }
  }, [copiedUserData, open, tags]);

  // تنظیم فرم با استفاده از Formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        // اعتبارسنجی‌های اضافی
        if (values.roleName === TypeUser.CUSTOMER && !isActuallyEdit) {
          // برای مشتریان جدید، نام کاربری و رمز عبور خودکار تولید می‌شود
          const randomNumber = Math.floor(Math.random() * 900) + 100;
          values.userName = values.phoneNumber + randomNumber;
          values.password = values.nationalCode + "Tekno@@";
        } else if (values.userName === "") {
          formik.setFieldError("userName", "نام کاربری الزامی است");
          return;
        } else if (!isActuallyEdit && values.password === "") {
          formik.setFieldError("password", "گذرواژه الزامی است");
          return;
        } else if (
          !isActuallyEdit &&
          values.password !== values.confirmedPassword
        ) {
          formik.setFieldError(
            "confirmedPassword",
            "گذرواژه و تکرار گذرواژه باید برابر باشند"
          );
          return;
        } else if (
          isActuallyEdit &&
          values.newPassword !== values.confirmedPassword
        ) {
          formik.setFieldError(
            "confirmedPassword",
            "گذرواژه جدید و تکرار گذرواژه باید برابر باشند"
          );
          return;
        }

        // بررسی یکتا بودن شماره پرونده (فقط اگر وارد شده باشد)
        if (values.caseNumber && values.caseNumber.trim()) {
          try {
            const checkResult = await checkCaseNumberUnique({
              caseNumber: values.caseNumber,
              excludeUserId: isActuallyEdit ? userData?.id : undefined,
            });

            if (!checkResult.data) {
              formik.setFieldError("caseNumber", "شماره پرونده تکراری است");
              return;
            }
          } catch (error) {
            console.error("خطا در بررسی یکتا بودن شماره پرونده:", error);
            // در صورت خطا در بررسی، اجازه ادامه می‌دهیم و سرور بررسی خواهد کرد
          }
        }

        // تبدیل تاریخ تولد
        let bDate;
        if (values.birthDate) {
          bDate = new Date(values.birthDate);
        }

        // تبدیل نوع مشتری
        const coType =
          values.typeUser !== "true" ? values.coType === "true" : undefined;

        // تبدیل تگ‌ها به رشته
        let tagsRefString;
        if (values.tagRef.length > 0) {
          const tagsRef: string[] = values.tagRef.map((row: any) => row.value);
          tagsRefString = tagsRef.join(",");
        } else {
          tagsRefString = undefined;
        }

        // آماده‌سازی داده‌ها برای ارسال
        const userValues = {
          ...values,
          birthDate: bDate,
          tagRef: tagsRefString,
          gender: values.gender === "true",
          typeUser: values.typeUser === "true",
          coType,
          isActive: String(values.isActive) === "true",
        };
        console.log("🚀 ~ NewUser ~ userValues:", userValues);

        // ارسال درخواست
        let res: any;
        if (!isActuallyEdit) {
          res = await insertAsync(userValues);
        } else {
          res = await updateAsync({ ...userValues, id: userData?.id });
        }

        if (res.status === true) {
          refetch(); // به‌روزرسانی لیست کاربران
          // در حالت ویرایش، اطلاعات کاربر خاص را هم به‌روزرسانی کن
          if (isActuallyEdit) {
            refetchUserData();
          }
          handleSendMessage(
            notifications,
            isActuallyEdit
              ? "اطلاعات با موفقیت ویرایش شد!"
              : "کاربر جدید با موفقیت ثبت شد!",
            "success"
          );
        } else {
          if (res.data === "userName already Exist!") {
            handleSendMessage(
              notifications,
              "کاربری با این شماره موبایل قبلا ثبت نام شده است!",
              "error"
            );
          } else if (res.data === "شماره پرونده تکراری است!") {
            handleSendMessage(
              notifications,
              "شماره پرونده تکراری است!",
              "error"
            );
          } else {
            handleSendMessage(notifications, "خطا در ذخیره سازی!", "error");
          }
        }
      } catch (error) {
        console.log("خطا در ارسال فرم:", error);
        handleSendMessage(notifications, "خطا در ذخیره سازی!", "error");
      }
    },
  });

  // تنظیم مقادیر فرم در حالت ویرایش
  useEffect(() => {
    if (isActuallyEdit && userData) {
      // تبدیل تگ‌ها از رشته به آرایه
      const TagsId: string[] = userData?.tagRef?.split(",") || [];

      const listOfSelectedTags =
        TagsId.length > 0
          ? tags.filter((tag: { value: number }) =>
              TagsId.includes(tag.value?.toString())
            )
          : [];

      // تنظیم مقادیر فرم
      formik.setValues({
        ...userData,
        roleName: userRole,
        isSendBalanceSms: userData?.isSendBalanceSms ? "true" : "false",
        gender: userData?.gender ? "true" : "false",
        typeUser: userData?.typeUser ? "true" : "false",
        coType: userData?.coType ? "true" : "false",
        isActive: userData?.isActive ? "true" : "false",
        birthDate: userData?.birthDate,
        tagRef: listOfSelectedTags,
      });
    } else if (isActuallyEdit && userRole) {
      // اگر در حالت ویرایش هستیم اما userData هنوز لود نشده، فقط roleName را تنظیم کنیم
      formik.setFieldValue("roleName", userRole);
    }
  }, [userData, userRef, isActuallyEdit, userRole, tags]); // حذف formik از dependency array

  // تنظیم roleName وقتی دیالوگ باز می‌شود
  useEffect(() => {
    if (open && userRole) {
      formik.setFieldValue("roleName", userRole);
    }
  }, [open, userRole]);

  // مدیریت بستن خودکار دیالوگ
  const [closeFrom, setCloseForm] = useState(false);
  const handleSaveAndClose = useCallback(async () => {
    await formik.submitForm();
    setCloseForm(true);
  }, [formik]);

  useEffect(() => {
    if (
      !formik.isSubmitting &&
      Object.keys(formik.errors).length === 0 &&
      formik.submitCount > 0 &&
      closeFrom
    ) {
      handleClose();
    }
  }, [
    formik.isSubmitting,
    formik.errors,
    formik.submitCount,
    closeFrom,
    handleClose,
  ]);

  return (
    <React.Fragment>
      <Dialog open={open} onClose={handleClose} fullScreen>
        <DialogTitle>
          {userFetchLoading
            ? "در حال بارگیری اطلاعات ..."
            : isActuallyEdit
              ? "کاربر " + userData?.fName + " " + userData?.lName
              : copiedUserData
                ? "کپی کاربر " +
                  copiedUserData.fName +
                  " " +
                  copiedUserData.lName
                : " کاربر جدید"}
        </DialogTitle>
        <DialogContent>
          <TabInfoUser
            refetchUser={refetchUserData}
            onClose={handleClose}
            usersData={usersData}
            deletedUserAsync={deletedUserAsync}
            insertAsync={insertAsync}
            updateAsync={updateAsync}
            deleteLoading={isDeletedUserLoading}
            insertLoading={isPending}
            updateLoading={isEditLoading}
            formik={formik}
            userData={userData}
            isEdit={isActuallyEdit}
            tags={tags}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            size="small"
            variant="contained"
            onClick={handleClose}
          >
            انصراف
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default NewUser;
